/**
 * Live: every session running right now, updating as verdicts land.
 *
 * The list comes from the REST call; the WebSocket org feed moves rows as
 * events arrive (a completed session leaves this page and appears under
 * Sessions). A connection pill says whether the feed is live, and the page
 * keeps polling when it is not, so the numbers are never more than a few
 * seconds old either way.
 *
 * A hundred rows that all say the same thing is a list nobody reads, so the
 * page opens with what is actually actionable — who is stuck on retakes, who
 * has not moved in a while — and the filter above the list narrows to it.
 */
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { api, MOCK_MODE, wsHost } from '../api';
import type { SessionRow } from '../api/types';
import { loadAuth } from '../auth/auth';
import {
  EmptyState,
  ErrorState,
  Initials,
  Page,
  PageHeader,
  Panel,
  Progress,
  Segmented,
  SkeletonRows,
  StatusBadge,
  timeAgo,
} from '../components/patterns';
import { href } from '../router';
import { useLiveFeed } from '../state/useLiveFeed';

const EMPTY: SessionRow[] = [];

/** No verdict for this long on a running session reads as stalled. */
const STALLED_MS = 20 * 60 * 1000;

type Filter = 'all' | 'attention' | 'fresh';

export function LiveSessions() {
  const { jwt, orgId } = useMemo(() => {
    const auth = loadAuth();
    return { jwt: auth?.jwt ?? '', orgId: auth?.org.id ?? '' };
  }, []);
  const [filter, setFilter] = useState<Filter>('all');

  const listQ = useQuery({
    queryKey: ['sessions', 'live'],
    queryFn: () => api.listSessions({ limit: 200 }),
    refetchInterval: 10_000,
  });
  const initialSessions = listQ.data?.sessions ?? EMPTY;
  const { state, connection } = useLiveFeed({ wsHost, token: jwt, orgId, initialSessions });

  // Something happened on the feed: refresh the REST view so step tallies
  // (which the feed does not carry) catch up. The refetch function is read
  // through a ref so the effect keys on the event id alone.
  const refetchRef = useRef(listQ.refetch);
  refetchRef.current = listQ.refetch;
  const lastEventId = state.lastEventId;
  useEffect(() => {
    if (lastEventId > 0) void refetchRef.current();
  }, [lastEventId]);

  const statusById = new Map(state.sessions.map((s) => [s.id, s.status]));
  const live = initialSessions.filter((s) => (statusById.get(s.id) ?? s.status) === 'active');

  const now = Date.now();
  const needsAttention = live.filter((s) => (s.retries ?? 0) > 0 || isStalled(s, now));
  const fresh = live.filter((s) => now - Date.parse(s.startedAt) < 15 * 60 * 1000);
  const rows = filter === 'attention' ? needsAttention : filter === 'fresh' ? fresh : live;

  return (
    <Page>
      <PageHeader
        eyebrow="Live"
        title={live.length === 1 ? '1 session running' : `${live.length} sessions running`}
        lede="Verdicts and step changes appear here as they happen."
        actions={
          MOCK_MODE ? null : connection === 'open' ? (
            <StatusBadge tone="verified" live>
              Live feed
            </StatusBadge>
          ) : connection === 'paused' ? (
            <StatusBadge tone="retry">Feed paused, polling</StatusBadge>
          ) : (
            <StatusBadge>Connecting…</StatusBadge>
          )
        }
      />

      <Panel
        flush
        bodyClassName="min-h-0"
        title={`${rows.length} shown`}
        description={
          needsAttention.length > 0
            ? `${needsAttention.length} need a look: retakes or no progress in ${Math.round(STALLED_MS / 60000)} minutes.`
            : 'Nothing is stuck.'
        }
        action={
          <Segmented
            ariaLabel="Filter live sessions"
            value={filter}
            onChange={setFilter}
            options={[
              { value: 'all', label: `All ${live.length}` },
              { value: 'attention', label: `Needs a look ${needsAttention.length}` },
              { value: 'fresh', label: `Just started ${fresh.length}` },
            ]}
          />
        }
      >
        {listQ.isLoading ? (
          <SkeletonRows rows={5} />
        ) : listQ.error ? (
          <ErrorState error={listQ.error} retry={() => void listQ.refetch()} />
        ) : rows.length === 0 ? (
          <EmptyState
            title={filter === 'all' ? 'Nothing running right now' : 'Nothing matches that filter'}
            body={
              filter === 'all'
                ? 'When a technician starts a procedure on the glasses, it shows up here within seconds.'
                : 'Every running session is moving along.'
            }
            action={
              filter === 'all' ? (
                <a className="text-support font-semibold text-field" href={href({ name: 'sessions' })}>
                  See past sessions
                </a>
              ) : null
            }
          />
        ) : (
          <ul className="divide-y divide-line border-t border-line">
            {rows.map((s) => (
              <LiveRow
                key={s.id}
                session={s}
                lastMessage={state.feed[s.id]?.at(-1)?.message}
                stalled={isStalled(s, now)}
              />
            ))}
          </ul>
        )}
      </Panel>
    </Page>
  );
}

function isStalled(s: SessionRow, now: number): boolean {
  return (s.verifiedSteps ?? 0) === 0 && now - Date.parse(s.startedAt) > STALLED_MS;
}

function LiveRow({
  session,
  lastMessage,
  stalled,
}: {
  session: SessionRow;
  lastMessage?: string;
  stalled: boolean;
}) {
  const total = session.totalSteps ?? 0;
  const done = session.verifiedSteps ?? 0;
  const name = session.technicianName ?? 'Technician';
  const retries = session.retries ?? 0;

  return (
    <li>
      <a
        href={href({ name: 'session', id: session.id })}
        className="flex items-center gap-3 px-4 py-2.5 transition-colors hover:bg-muted sm:px-5"
      >
        <Initials name={name} className="size-7" />

        <div className="min-w-0 flex-1">
          <div className="truncate text-support">
            <span className="font-semibold text-ink">{name}</span>
            <span className="text-ink-dim">
              {' · '}
              {session.equipmentName ?? session.procedureName ?? 'Procedure'}
            </span>
          </div>
          <div className="truncate text-meta text-ink-faint">
            {lastMessage ?? `Started ${timeAgo(session.startedAt)}`}
          </div>
        </div>

        {/* The numbers. Below sm they drop under the name rather than
            squeezing the title to nothing. */}
        <div className="hidden w-36 shrink-0 sm:block">
          <div className="mb-1 text-right text-meta text-ink-dim">
            {total > 0 ? `${done} / ${total} steps` : `${done} steps`}
          </div>
          <Progress value={total > 0 ? done / total : 0} />
        </div>

        <div className="flex shrink-0 items-center gap-2">
          {retries > 0 ? (
            <StatusBadge tone="retry">
              {retries} {retries === 1 ? 'retake' : 'retakes'}
            </StatusBadge>
          ) : null}
          {stalled ? <StatusBadge tone="retry">Stalled</StatusBadge> : null}
          <StatusBadge tone="field" live className="hidden md:inline-flex">
            Live
          </StatusBadge>
        </div>
      </a>
    </li>
  );
}
